import { useCallback, useState, MutableRefObject } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { reportErrorToFaro } from '@sendible/common';
import { useToastNotificationContext } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useReportingContext, buildGridLayoutFromModules } from '../../../context';
import { resolveProfiles } from '../../../helpers';
import { getPersistedModules as transformPersistedModules } from '../getPersistedModules';
import { useCreateReport, useDuplicateReport, useUpdateReport, useDeleteReport } from '../../../../../models/reporting';

type TransformedReport = {
  id: string;
  modules: ReportModule[];
  workspaceId: number;
  name: string;
  profileIds: string[];
  updatedAt?: string;
  updated_at?: string;
  createdAt?: string;
  created_at?: string;
};

type UseReportActionsParams = {
  isNewReport: boolean;
  report: TransformedReport | null;
  allProfiles: ReportProfile[] | undefined;
  moduleDefinitions: ModuleDefinition[] | undefined;
  reportName: string;
  setReportName: (name: string) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  hasSnapshotted: MutableRefObject<string | null>;
  latestSnapshotState: MutableRefObject<() => void>;
  isOpenedFromTemplate: boolean;
};

export const useReportActions = ({
  isNewReport,
  report,
  allProfiles,
  moduleDefinitions,
  reportName,
  setReportName,
  isEditing,
  setIsEditing,
  hasSnapshotted: hasSnapshottedRef, // <-- Renamed to end with Ref
  latestSnapshotState: latestSnapshotStateRef, // <-- Renamed to end with Ref
  isOpenedFromTemplate,
}: UseReportActionsParams) => {
  const { t } = useTranslation('reports');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [{ user }] = useBridgeContext();
  const { showToastNotification } = useToastNotificationContext();

  const { setReportId, setProfiles, setModules, modules, profiles, updateGridLayout, isDirty, snapshotState, period, setIsLoadedFromTemplate } =
    useReportingContext();

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [discardTarget, setDiscardTarget] = useState<'navigation' | 'viewMode'>('navigation');

  const createReport = useCreateReport();
  const duplicateReport = useDuplicateReport();
  const updateReport = useUpdateReport(id && id !== 'new' ? id : '');
  const deleteReport = useDeleteReport(id && id !== 'new' ? id : '');

  const getPersistedModules = useCallback(() => transformPersistedModules(modules, moduleDefinitions ?? undefined), [modules, moduleDefinitions]);

  const handleBack = () => {
    if (isEditing && isDirty) {
      setDiscardTarget('navigation');
      setShowDiscardModal(true);

      return;
    }

    navigate('/reporting');
  };

  const handleCancelEdit = () => {
    if (isNewReport) {
      if (isDirty) {
        setDiscardTarget('navigation');
        setShowDiscardModal(true);

        return;
      }

      navigate('/reporting');

      return;
    }

    if (isDirty) {
      setDiscardTarget('viewMode');
      setShowDiscardModal(true);

      return;
    }

    setIsEditing(false);
  };

  const handleRevertToViewMode = () => {
    if (isNewReport) {
      setShowDiscardModal(false);
      navigate('/reporting');

      return;
    }

    if (report) {
      setReportId(report.id);
      setModules(report.modules ?? []);
      setReportName(report.name);
      updateGridLayout(buildGridLayoutFromModules(report.modules ?? [], moduleDefinitions));

      const reportProfileIds = report.profileIds ?? [];

      setProfiles(resolveProfiles(reportProfileIds, allProfiles ?? []));
      hasSnapshottedRef.current = null; // <-- Mutation is now valid
      requestAnimationFrame(() => latestSnapshotStateRef.current());
    }

    setIsEditing(false);
    setShowDiscardModal(false);
  };

  const handleConfirmDiscard = () => {
    if (discardTarget === 'viewMode') {
      handleRevertToViewMode();

      return;
    }

    setShowDiscardModal(false);
    navigate('/reporting');
  };

  const handleDelete = useCallback(async () => {
    if (isNewReport) {
      navigate('/reporting');

      return;
    }

    try {
      await deleteReport.mutateAsync({});
      navigate('/reporting');
    } catch (error) {
      reportErrorToFaro(error);
    }
  }, [isNewReport, deleteReport, navigate]);

  const handleDuplicate = useCallback(async () => {
    if (!id || id === 'new') return;

    try {
      const result = await duplicateReport.mutateAsync({
        reportId: id,
      });

      if (result?.id) {
        navigate(`/reporting/${result.id}`);
      }
    } catch (error) {
      reportErrorToFaro(error, 'ReportActions handleDuplicate');
    }
  }, [duplicateReport, id, navigate]);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      if (isNewReport) {
        const result = await createReport.mutateAsync({
          workspaceId: user.userId,
          name: reportName || (t('new_reports_untitled_report') as string),
          period,
          profileIds: profiles.filter((p) => !p.isUnknown).map((p) => p.id),
          modules: getPersistedModules(),
        });

        if (result?.id) {
          navigate(`/reporting/${result.id}`, { replace: true });
        }
      } else {
        await updateReport.mutateAsync({
          name: reportName || (t('new_reports_untitled_report') as string),
          period,
          profileIds: profiles.filter((p) => !p.isUnknown).map((p) => p.id),
          modules: getPersistedModules(),
        });
      }

      setSaveSuccess(true);
      snapshotState();

      if (isOpenedFromTemplate) {
        setIsLoadedFromTemplate(false);
        setIsEditing(false);
      } else if (!isNewReport) {
        setIsEditing(false);
      }
      setTimeout(() => {
        setSaveSuccess(false);
      }, 1500);
    } catch (error) {
      reportErrorToFaro(error, 'ReportActions handleSave');
      showToastNotification({
        notificationText: t('new_reports_save_error') as string,
        type: NotificationTypeEnum.Error,
        icon: true,
        showCloseButton: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    isNewReport,
    isOpenedFromTemplate,
    reportName,
    period,
    createReport,
    updateReport,
    getPersistedModules,
    navigate,
    snapshotState,
    setIsLoadedFromTemplate,
  ]);

  return {
    isSaving,
    saveSuccess,
    isDirty,
    showDiscardModal,
    showDeleteModal,
    setShowDiscardModal,
    setShowDeleteModal,
    handleBack,
    handleCancelEdit,
    handleConfirmDiscard,
    handleDelete,
    handleDuplicate,
    handleSave,
  };
};
